<template>
    <div v-if="history" class="card">
        <div class="card-header bg-white">
            <h5 class="card-title">{{ (history.id>0 ? 'Redigera historik': 'Ny historik') }}</h5>
        </div>
        <div class="card-body">
            <form @submit.prevent="saveHistory">
                <ValidationObserver ref="historyform">
                    <div class="row">
                        <div class="col-6">
                            <ValidationProvider vid="date_time" name="Klockslag" rules="required" v-slot="{ errors }">
                                <div class="form-group">
                                    <label>Klockslag <span class="text-danger">*</span></label>
                                    <v-date-picker v-model="history.date_time" mode="dateTime" :is24hr="true" :model-config="{ type: 'string', mask: 'YYYY-MM-DD HH:mm' }" :masks="{ input: 'YYYY-MM-DD HH:mm' }">
                                        <template v-slot="{ togglePopover }">
                                            <input class="form-control" :value="history.date_time" @focus="togglePopover()">
                                        </template>
                                    </v-date-picker>
                                    <span class="validation-invalid-label">{{ errors[0] }}</span>
                                </div>
                            </ValidationProvider>
                        </div>
                        <div class="col-6">
                            <ValidationProvider vid="user_id" name="Säljare" rules="required" v-slot="{ errors }">
                                <div class="form-group">
                                    <label>Säljare <span class="text-danger">*</span></label>
                                    <v-select
                                        v-model="history.user_id"
                                        :options="select_users"
                                        placeholder="Välj..."
                                        :reduce="opt => opt.id"
                                        label="name"
                                    ></v-select>
                                    <span class="validation-invalid-label">{{ errors[0] }}</span>
                                </div>
                            </ValidationProvider>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-6">
                            <ValidationProvider vid="type_id" name="Typ" rules="required" v-slot="{ errors }">
                                <div class="form-group">
                                    <label>Typ <span class="text-danger">*</span></label>
                                    <v-select
                                        v-model="history.type_id"
                                        :options="select_history_types"
                                        :reduce="opt => opt.id"
                                        placeholder="Välj typ..."
                                    ></v-select>
                                    <span class="validation-invalid-label">{{ errors[0] }}</span>
                                </div>
                            </ValidationProvider>
                        </div>
                        <div class="col-6">
                            <ValidationProvider vid="company_contact_id" name="Kontakt" rules="" v-slot="{ errors }">
                                <div class="form-group">
                                    <label>Kontakt</label>
                                    <v-select
                                        v-model="history.company_contact_id"
                                        :options="company.contacts"
                                        placeholder="Välj..."
                                        :reduce="opt => opt.id"
                                        label="name"
                                    ></v-select>
                                    <span class="validation-invalid-label">{{ errors[0] }}</span>
                                </div>
                            </ValidationProvider>
                        </div>
                    </div>
                    <ValidationProvider vid="subject" name="Ämne" rules="required" v-slot="{ errors }">
                        <div class="form-group">
                            <label>Ämne <span class="text-danger">*</span></label>
                            <v-select
                                v-model="history.subject"
                                :options="select_history_subjects"
                                :reduce="opt => opt.label"
                                placeholder="Välj/Skriv..."
                                taggable
                            ></v-select>
                            <span class="validation-invalid-label">{{ errors[0] }}</span>
                        </div>
                    </ValidationProvider>
                    <ValidationProvider vid="text" name="Text" rules="" v-slot="{ errors }">
                        <div class="form-group">
                            <label>Text</label>
                            <textarea class="form-control" rows="10" v-model="history.text" v-bind:class="{ 'border-danger':errors[0] }"></textarea>
                            <span class="validation-invalid-label">{{ errors[0] }}</span>
                        </div>
                    </ValidationProvider>
                </ValidationObserver>
            </form>
            <div class="text-right">
                <limitless-button ref="btnsavehistory" type="button" @click="saveHistory" :busy="history_busy" icon="icon-floppy-disk">SPARA</limitless-button>
            </div>
        </div>
    </div>
    <tb-skeleton v-else :height="567" :theme="'opacity'" :shape="'radius'" bg-color="#dcdbdc" style="margin-bottom: 1.25rem;"></tb-skeleton>
</template>

<script>
import vSelect from 'vue-select';
import LimitlessButton from '@components/Limitless/LimitlessButton';
import VDatePicker from 'v-calendar/lib/components/date-picker.umd';
import getError from '@services/ErrorHelper';
import apiClient from '@services/ApiClient';

export default {
    name: 'HistoryForm',
    components: {
        vSelect,
        VDatePicker,
        LimitlessButton,
    },
    props: {
        historyId: {
            type: Number,
            default: 0,
        },
        company: {
            type: Object,
            default() {
                return {};
            },
        },
        users: {
            type: Array,
            default() {
                return [];
            },
        },
        strings: {
            type: Object,
            default() {
                return {};
            },
        },
    },
    data: () => ({
        history: null,
        history_busy: false,
    }),
    mounted() {
        this.getHistory();
    },
    methods: {
        getHistory() {
            const vm = this;
            vm.history_busy = true;
            apiClient.get(`/vue/sales/crm/history/${this.historyId}`)
                .then((response) => {
                    vm.history = response.data;
                    if (vm.company) {
                        vm.history.company_id = vm.company.id;
                    }
                })
                .finally(() => {
                    vm.history_busy = false;
                });
        },
        async saveHistory() {
            const vm = this;
            const isValid = await vm.$refs.historyform.validate();
            if (isValid) {
                vm.history_busy = true;
                await apiClient.post(`/vue/sales/crm/history/${vm.history.id}`, vm.history)
                    .then((response) => {
                        vm.history = response.data;
                        vm.$emit('saved', response.data);
                        vm.$refs.btnsavehistory.triggerSaved();
                    })
                    .catch((error) => {
                        const parsederror = getError(error);
                        if (typeof parsederror === 'object') {
                            vm.$refs.historyform.setErrors(parsederror);
                        }
                        vm.$refs.btnsavehistory.triggerFailed();
                    })
                    .finally(() => {
                        vm.history_busy = false;
                    });
            } else {
                vm.$refs.btnsavehistory.triggerFailed();
            }
        },
    },
    computed: {
        select_history_types() {
            if (this.strings[502]) {
                return this.strings[502].map((string) => ({ id: string.id, label: string.string }));
            }
            return [];
        },
        select_history_subjects() {
            const arr = [];
            if (this.strings[3]) {
                this.strings[3].forEach((string) => {
                    if (!arr.find((s) => s.label === string.string)) {
                        arr.push({ label: string.string });
                    }
                });
            }
            if (this.history) {
                if (!arr.find((s) => s.label === this.history.subject)) {
                    arr.push({ label: this.history.subject });
                }
            }
            return arr;
        },
        select_users() {
            const arr = [];
            Object.values(this.company.sellers).forEach((seller) => {
                arr.push({ id: seller.id, name: '☆ ' + seller.name });
            });
            Object.values(this.users).forEach((seller) => {
                if (seller && seller.permissions && seller.permissions.includes('red_sales_crm_show_others')) {
                    if (!arr.find((s) => s.id === seller.id)) {
                        arr.push({ id: seller.id, name: seller.name });
                    }
                }
            });
            return arr;
        },
    },
};
</script>

<style scoped>

</style>
